import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import * as Icons from 'assets/svg/sprite'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import colors from 'styles/colors'

interface ICreateTicketModalProps {
    isOpen: boolean
    handleSetSubject: (subject: string) => void
    handleClose: () => void
}

interface Topic {
    title: string
    icon?: JSX.Element
    questions?: string[]
    children?: {
        title: string
        questions: string[]
    }[]
}

export const CreateTicketModal = ({
    isOpen,
    handleSetSubject,
    handleClose
}: ICreateTicketModalProps) => {
    const { t } = useTranslation()
    const [searchTerm, setSearchTerm] = useState('')

    const topics: Topic[] = useMemo(
        () => [
            {
                title: t('generalQuestions'),
                icon: (
                    <Icons.chatBubblesWithQuestion
                        color={`${colors.blueDark}`}
                    />
                ),
                questions: [
                    'TODO: General Questions 1',
                    'TODO: General Questions 2'
                ]
            },
            {
                title: t('fanQuestions'),
                icon: <Icons.IconThreeUsersOutline />,
                children: [
                    {
                        title: t('subscriptions'),
                        questions: [
                            t('whySubscribe'),
                            t('howToSubscribe'),
                            t('fondingAProfile'),
                            t('freeTrials'),
                            t('imUnableToSubscribeToAnAccount'),
                            t('iCannotFindMySubscription'),
                            t('thisPageIsNotAvailableHelp'),
                            t('cancelASubscription'),
                            t('autoRenewalSubscriptions')
                        ]
                    }
                ]
            },
            {
                title: 'Payment',
                questions: ['TODO: Payment 1', 'TODO: Payment 2']
            },
            {
                title: 'Tips',
                questions: ['TODO: Tips 1', 'TODO: Tips 2']
            }
        ],
        [t]
    )

    const normalizedSearch = searchTerm.trim().toLowerCase()

    const filteredTopics = useMemo(() => {
        if (!normalizedSearch) return topics

        return topics
            .map(topic => {
                const topicMatches = topic.title
                    .toLowerCase()
                    .includes(normalizedSearch)

                const filteredQuestions = topic.questions?.filter(question =>
                    question.toLowerCase().includes(normalizedSearch)
                )

                const filteredChildren = topic.children
                    ?.map(child => {
                        const childMatches = child.title
                            .toLowerCase()
                            .includes(normalizedSearch)

                        const matchingQuestions = child.questions.filter(
                            question =>
                                question
                                    .toLowerCase()
                                    .includes(normalizedSearch)
                        )

                        if (childMatches) {
                            return child
                        }

                        if (matchingQuestions.length > 0) {
                            return {
                                ...child,
                                questions: matchingQuestions
                            }
                        }

                        return null
                    })
                    .filter(
                        (
                            child
                        ): child is {
                            title: string
                            questions: string[]
                        } => child !== null
                    )

                if (topicMatches) {
                    return topic
                }

                if (
                    (filteredQuestions && filteredQuestions.length > 0) ||
                    (filteredChildren && filteredChildren.length > 0)
                ) {
                    return {
                        ...topic,
                        questions: filteredQuestions,
                        children: filteredChildren
                    }
                }

                return null
            })
            .filter((topic): topic is Topic => topic !== null)
    }, [topics, normalizedSearch])

    const selectSubject = (subject: string) => {
        handleSetSubject(subject)
        setSearchTerm('')
    }

    if (!isOpen) {
        return null
    }

    return (
        <StyledBackdrop onClick={handleClose}>
            <StyledWrapper onClick={e => e.stopPropagation()}>
                <StyledModalTitle>{t('selectTopic')}</StyledModalTitle>
                <StyledSearchWrapper>
                    <StyledSearchInput
                        type='text'
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder='Search topics'
                        autoFocus
                    />
                </StyledSearchWrapper>

                {filteredTopics.length > 0 ? (
                    filteredTopics.map(topic => (
                        <TopicBlock key={topic.title}>
                            <ActionCard
                                customStyle={css`
                                    background: ${colors.white89deg};
                                    box-shadow: 0 0.4rem 3rem
                                        rgba(0, 0, 0, 0.05);
                                    border-radius: 2rem;
                                `}
                                icon={topic.icon}
                                iconSize='small'
                                text={topic.title}
                                dropDownContent={
                                    <>
                                        {topic.questions &&
                                            topic.questions.length > 0 && (
                                                <StyledDropdownList>
                                                    {topic.questions.map(
                                                        question => (
                                                            <StyledDropdownListItem
                                                                key={question}
                                                                onClick={() =>
                                                                    selectSubject(
                                                                        question
                                                                    )
                                                                }
                                                            >
                                                                {question}
                                                            </StyledDropdownListItem>
                                                        )
                                                    )}
                                                </StyledDropdownList>
                                            )}

                                        {topic.children?.map(child => (
                                            <ActionCard
                                                key={child.title}
                                                customStyle={css`
                                                    background: ${colors.white};
                                                    box-shadow: 0 0.4rem 3rem
                                                        rgba(0, 0, 0, 0.05);
                                                    border-radius: 0;
                                                    padding: 1.5rem 0 1.5rem 1rem;
                                                `}
                                                isEmphasizedText
                                                text={child.title}
                                                dropDownContent={
                                                    <StyledDropdownList>
                                                        {child.questions.map(
                                                            question => (
                                                                <StyledDropdownListItem
                                                                    key={
                                                                        question
                                                                    }
                                                                    onClick={() =>
                                                                        selectSubject(
                                                                            question
                                                                        )
                                                                    }
                                                                >
                                                                    {question}
                                                                </StyledDropdownListItem>
                                                            )
                                                        )}
                                                    </StyledDropdownList>
                                                }
                                            />
                                        ))}
                                    </>
                                }
                            />
                        </TopicBlock>
                    ))
                ) : (
                    <StyledNoResults>
                        No matching topics found
                    </StyledNoResults>
                )}
            </StyledWrapper>
        </StyledBackdrop>
    )
}

const StyledBackdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 99;
    background: rgba(0, 0, 0, 0.15);
`

const StyledWrapper = styled.div`
    background-color: ${colors.white};
    border-radius: 2rem;
    box-shadow: 0 2rem 4rem rgba(17, 29, 41, 0.6);
    padding: 1.5rem 2rem 2rem;

    position: fixed;
    top: 6rem;
    bottom: 6rem;
    left: 2rem;
    right: 2rem;
    padding:2rem;
    z-index: 100;

    overflow-x: hidden;
    overflow-y: auto;

    @media (min-width: 768px) {
        top: 50%;
        bottom: auto;
        left: 50%;
        right: auto;

        width: calc(100% - 4rem);
        max-width: 60rem;
        max-height: 55rem;

        transform: translate(-50%, -50%);
    }
`

const StyledSearchWrapper = styled.div`
    position: sticky;
    top: 0;
    z-index: 5;

    display: flex;
    align-items: center;
    gap: 1rem;

    background: ${colors.white};
    padding: 0.5rem 0 1.5rem;
`


const StyledSearchInput = styled.input`
    width: 100%;
    height: 4.8rem;

    border: 0.1rem solid #d9d9d9;
    border-radius: 1.5rem;

    padding: 0 1.5rem;

    font-size: 1.5rem;

    outline: none;

    &::placeholder {
        color: #a7a7a7;
    }

    &:focus {
        border-color: ${colors.blueDark};
    }
`

const TopicBlock = styled.div`
    & + & {
        margin-top: 1rem;
    }
`

const StyledDropdownList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 1rem 0 0;
    display: flex;
    flex-direction: column;
`

const StyledDropdownListItem = styled.li`
    color: ${colors.blueDark};
    cursor: pointer;
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 1.7rem;
    letter-spacing: 0.02em;
    margin: 1rem 0;

    &:hover {
        opacity: 0.7;
    }
`

const StyledNoResults = styled.div`
    width: 100%;
    padding: 4rem 2rem;
    text-align: center;
    font-size: 1.6rem;
    opacity: 0.6;
`
const StyledModalTitle = styled.h2`
    margin: 0 0 1.5rem;
    color: ${colors.blueDark};
    font-size: 2rem;
    font-weight: 600;
`